'use client'
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import styled from 'styled-components';
import { useConfig } from '@/contexts/ConfigContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUI } from '@/contexts/UIContext';
import { usePro } from '@/contexts/ProContext';
import { storageService } from '@/utils/storageService';
import { getBridge } from '@/utils/bridge';
import { track } from '@/utils/analytics';
import {
    MoreVert, CheckBox, CheckBoxOutlineBlank,
    Lock, LockOpen, ContentCopy, Delete as DeleteIcon, Edit,
    Add, CheckCircle
} from '@mui/icons-material';
import KeySelector from '@/components/CustomInput/KeySelector';
import CrosshairSidebarPreview from '@/components/Layouts/Sidebar/Previews/CrosshairSidebarPreview';
import MousePreview from '@/components/Layouts/Sidebar/Previews/MousePreview';
import ViewmodelPreview from '@/components/Layouts/Sidebar/Previews/ViewmodelPreview';
import HudPreview from '@/components/Layouts/Sidebar/Previews/HudPreview';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 100%;
    color: white;
    text-align: center;
    padding: 0px 20px 40px 20px;
    box-sizing: border-box;
    overflow-y: auto;
`;


/* Table Styles */
const TableContainer = styled.div`
    width: 100%;
    max-width: 1100px;
    background: rgba(17, 24, 39, 0.6);
    overflow: visible;
    backdrop-filter: blur(10px);
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    text-align: left;
`;

const Thead = styled.thead`
    background: rgba(0, 0, 0, 0.3);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Th = styled.th`
    padding: 16px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(255, 255, 255, 0.4);
    
    &:first-child { width: 48px; text-align: center; }
    &:last-child { width: 48px; }
`;

const Tr = styled.tr`
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
    transition: all 0.2s;
    background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.08)' : (props.$isActive ? 'rgba(74, 222, 128, 0.03)' : 'transparent')};
    border-left: 3px solid ${props => props.$isActive ? '#4ade80' : 'transparent'};

    &:hover {
        background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.12)' : (props.$isActive ? 'rgba(74, 222, 128, 0.08)' : 'rgba(255, 255, 255, 0.02)')};
        //transform: translateX(2px);
    }
    
    &:last-child { border-bottom: none; }
`;

const Td = styled.td`
    padding: 14px 16px;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
    vertical-align: middle;
    
    &:first-child { text-align: center; }
`;

const ConfigName = styled.div`
    font-weight: 600;
    color: white;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 360px;
`;

const Badge = styled.span`
    font-size: 0.65rem;
    padding: 2px 8px;
    border-radius: 12px;
    background: ${props => props.$color || 'rgba(255,255,255,0.1)'};
    color: ${props => props.$textColor || 'white'};
    border: 1px solid ${props => props.$borderColor || 'transparent'};
    text-transform: uppercase;
`;

const IconButton = styled.button`
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    
    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
    }
`;

const EditButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(99, 102, 241, 0.1);
    color: #818cf8;
    border: 1px solid rgba(99, 102, 241, 0.2);
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 0.7rem;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:hover {
        background: #6366f1;
        color: white;
        border-color: #6366f1;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
    }
    
    &:active {
        transform: translateY(0);
    }

    svg {
        font-size: 14px;
    }
`;

/* Menu Styles */
const MenuOverlay = styled.div`
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    z-index: 99;
`;

const MenuDropdown = styled.div`
    position: absolute;
    right: 48px;
    background: #1f2937;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
    border-radius: 8px;
    padding: 4px;
    z-index: 800;
    min-width: 180px;
    display: flex;
    flex-direction: column;
`;

const MenuItem = styled.button`
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.8);
    padding: 10px 12px;
    text-align: left;
    font-size: 0.85rem;
    cursor: pointer;
    border-radius: 4px;
    display: flex;
    align-items: left;
    justify-content: flex-start;
    gap: 10px;
    transition: all 0.2s;
    width: 100%;
    
    &:hover {
        background: rgba(255, 255, 255, 0.05);
        color: white;
    }

    &.danger {
        color: #fca5a5;
        &:hover { background: rgba(239, 68, 68, 0.1); }
    }
`;

/* Batch Bar */
const BatchBar = styled.div`
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%) translateY(${props => props.$visible ? '0' : '200%'});
    opacity: ${props => props.$visible ? '1' : '0'};
    pointer-events: ${props => props.$visible ? 'auto' : 'none'};
    background: #6366f1;
    padding: 12px 24px;
    border-radius: 100px;
    box-shadow: 0 10px 40px rgba(99, 102, 241, 0.4);
    display: flex;
    align-items: center;
    gap: 24px;
    z-index: 50;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    border: 1px solid rgba(255,255,255,0.2);
`;

const BatchCount = styled.span`
    font-weight: 700;
    font-size: 0.9rem;
    color: white;
    padding-right: 12px;
    border-right: 1px solid rgba(255,255,255,0.2);
    min-width: 100px;
`;

const BatchActions = styled.div`
    display: flex;
    gap: 8px;
`;

const BatchBtn = styled.button`
    background: rgba(255,255,255,0.1);
    border: none;
    color: white;
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background 0.2s;

    &:hover { background: rgba(255,255,255,0.25); }
    
    &.danger {
        background: rgba(0,0,0,0.2);
        color: #fca5a5;
        &:hover { background: rgba(220, 38, 38, 0.4); color: white; }
    }
`;

const ModulesSection = styled.div`
    width: 100%;
    max-width: 1100px;
    margin-bottom: 32px;
    display: flex;
    flex-direction: column;
`

const ModulesSectionTitle = styled.div`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: rgba(255, 255, 255, 0.3);
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    width: 100%;
`

const ModulesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 30px;

    & > * {
        height: 165px;
    }
`

const PreviewActionCard = styled.button`
    height: 165px;
    border-radius: 12px;
    border: 1px dashed ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.3)' : 'rgba(99, 102, 241, 0.73)'};
    background:
        linear-gradient(to bottom, transparent 30%, ${({ $variant }) => $variant === 'edit' ? 'rgba(6, 16, 10, 0.9)' : 'rgba(8, 10, 18, 0.9)'} 100%),
        repeating-linear-gradient(
            45deg,
            ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.04)' : 'rgba(99, 102, 241, 0.04)'} 0px,
            ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.04)' : 'rgba(99, 102, 241, 0.04)'} 1px,
            transparent 1px, transparent 12px
        ),
        repeating-linear-gradient(
            -45deg,
            ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.04)' : 'rgba(99, 102, 241, 0.04)'} 0px,
            ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.04)' : 'rgba(99, 102, 241, 0.04)'} 1px,
            transparent 1px, transparent 12px
        ),
        rgba(12, 14, 24, 0.6);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.6)' : 'rgba(99, 102, 241, 0.6)'};

    &:hover {
        border-color: ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.6)' : 'rgba(99, 102, 241, 0.6)'};
        color: ${({ $variant }) => $variant === 'edit' ? '#4ade80' : '#818cf8'};
        transform: translateY(-2px);
        box-shadow: 0 8px 24px ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.12)' : 'rgba(99, 102, 241, 0.15)'};
    }

    &:active { transform: translateY(0); }
`

const ActionRow = styled.div`
    width: 100%;
    max-width: 1100px;
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 20px;
`
const SidebarTitle = styled.h2`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 1.9rem;
    font-weight: 800;
    text-transform: uppercase;
    color: white;
    margin: 10px 0 12px 14px;
    letter-spacing: 1px;
    display: flex;
    align-items: center;
    
    span {
        color: #7260ff;
        text-shadow: 0 0 10px rgba(114, 96, 255, 0.6);
    }
`;
const GridActionBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 28px;
    padding-left: 10px;
    height: 56px;
    border-radius: 12px;
    border: 1px dashed ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.1)' : 'rgba(99, 102, 241, 0.1)'};
    background:
        linear-gradient(to right, transparent 30%, ${({ $variant }) => $variant === 'edit' ? 'rgba(8, 18, 12, 0.85)' : 'rgba(8, 10, 18, 0.85)'} 100%),
        repeating-linear-gradient(
            45deg,
            ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.04)' : 'rgba(99, 102, 241, 0.04)'} 0px,
            ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.04)' : 'rgba(99, 102, 241, 0.04)'} 1px,
            transparent 1px,
            transparent 12px
        ),
        repeating-linear-gradient(
            -45deg,
            ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.04)' : 'rgba(99, 102, 241, 0.04)'} 0px,
            ${({ $variant }) => $variant === 'edit' ? 'rgba(74, 222, 128, 0.04)' : 'rgba(99, 102, 241, 0.04)'} 1px,
            transparent 1px,
            transparent 12px
        ),
        rgba(12, 14, 24, 0.6);
    color: ${({ $variant, $locked }) =>
        $locked ? 'rgba(251, 191, 36, 0.7)'
        : $variant === 'edit' ? 'rgba(74, 222, 128, 0.7)'
        : 'rgba(99, 102, 241, 0.7)'};
    border-color: ${({ $locked }) => $locked ? 'rgba(251, 191, 36, 0.35)' : ''};
    cursor: ${({ $locked }) => $locked ? 'not-allowed' : 'pointer'};
    transition: all 0.2s ease;
    min-width: 200px;
    width: 100%;
    opacity: ${({ $locked }) => $locked ? 0.8 : 1};

    ${({ $variant }) => $variant === 'edit' ? ` background: none; ` : ''}

    &:hover {
        border-color: ${({ $variant, $locked }) =>
            $locked ? 'rgba(251, 191, 36, 0.55)'
            : $variant === 'edit' ? 'rgba(74, 222, 128, 0.4)'
            : 'rgba(99, 102, 241, 0.4)'};
        color: ${({ $variant, $locked }) =>
            $locked ? '#fbbf24'
            : $variant === 'edit' ? '#4ade80'
            : '#818cf8'};
        transform: ${({ $locked }) => $locked ? 'none' : 'translateY(-1px)'};
        box-shadow: ${({ $variant, $locked }) =>
            $locked ? '0 4px 14px rgba(251, 191, 36, 0.1)'
            : $variant === 'edit' ? '0 4px 14px rgba(74, 222, 128, 0.12)'
            : '0 4px 14px rgba(99, 102, 241, 0.15)'};
    }

    &:active { transform: ${({ $locked }) => $locked ? 'none' : 'translateY(0)'}; }
`

const GridBtnLabel = styled.span`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: inherit;
`

const GridBtnSub = styled.span`
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    color: rgba(255, 255, 255, 0.56);
    letter-spacing: 0.3px;
    display: block;
    margin-top: 1px;
`

const ManagerView = () => {
    const {
        setIsSaving, userConfigs, fetchUserConfigs,
        deleteConfig, duplicateConfig, getTemplateData,
        configName, loadUserConfig, setLauncherBind,
        configData, activeModules, focusModule
    } = useConfig();

    const handlePreviewClick = (module) => {
        if (focusModule) focusModule(module);
    };
    const router = useRouter();

    const { t } = useLanguage();
    const { showConfirm, showCreateConfig, showPrompt } = useUI();
    const { hasFeature } = usePro();
    // Crear/renombrar/duplicar/bloquear/eliminar. Editar la config activa es libre.
    const isPro = hasFeature('gestion_configs');

    const [selectedIds, setSelectedIds] = useState([]);
    const [openMenuId, setOpenMenuId] = useState(null);

    const configs = userConfigs || [];
    const isAllSelected = configs.length > 0 && selectedIds.length === configs.length;

    const handleSelectAll = (e) => {
        if (e) e.stopPropagation();
        if (isAllSelected) {
            setSelectedIds([]);
        } else {
            setSelectedIds(configs.map(c => c.id));
        }
    };

    const handleSelectOne = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(i => i !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    const handleActivate = (config) => {
        loadUserConfig(config);
    };

    const handleToggleLock = async (config) => {
        try {
            const newLocked = !config.isLocked;
            const updated = {
                ...config,
                isLocked: newLocked,
                settings: {
                    ...config.settings,
                    _meta: { ...config.settings?._meta, isLocked: newLocked }
                }
            };
            await storageService.saveConfig(updated);
            await fetchUserConfigs(true);
            setOpenMenuId(null);
        } catch (e) {
            console.error(e);
        }
    };

    const handleDelete = async (id) => {
        const confirmed = await showConfirm({
            title: t('DELETE_CONFIG'),
            message: t('DELETE_CONFIRM') || t('CONFIRM_DELETE'),
            variant: 'danger',
            confirmText: t('DELETE_ACTION')
        });

        if (confirmed) {
            await deleteConfig(id);
            setOpenMenuId(null);
            setSelectedIds(prev => prev.filter(pid => pid !== id));
        }
    };

    const handleDuplicate = async (id) => {
        const config = configs.find(c => c.id === id);
        if (!config) return;

        let baseName = `${config.name} ${t('DUPLICATED_SUFFIX')}`;
        // Ensure unique name default
        if (configs.some(c => c.name.toLowerCase() === baseName.toLowerCase())) {
            let counter = 2;
            while (configs.some(c => c.name.toLowerCase() === `${baseName} ${counter}`.toLowerCase())) {
                counter++;
            }
            baseName = `${baseName} ${counter}`;
        }

        const newName = await showPrompt({
            title: t('DUPLICATE_CONFIG_TITLE') || 'Duplicar Config',
            defaultValue: baseName,
            confirmText: t('DUPLICATE_ACTION') || 'Duplicar',
            placeholder: t('CONFIG_NAME_LABEL')
        });

        if (newName) {
            await duplicateConfig(id, newName);
            setOpenMenuId(null);
        }
    };

    const handleCreateConfig = async () => {
        let baseName = t('NEW_CONFIG_DEFAULT_NAME') || 'New Config';
        let nextNum = 1;
        const existingNames = configs.map(c => c.name.toLowerCase());

        let candidate = baseName;
        while (existingNames.includes(candidate.toLowerCase())) {
            nextNum++;
            candidate = `${baseName} ${nextNum}`;
        }

        const result = await showCreateConfig({
            title: t('CREATE_CONFIG'),
            confirmText: t('CREATE_ACTION') || 'Crear',
            defaultValue: candidate,
            userConfigs: configs
        });

        if (result && result.name) {
            if (setIsSaving) setIsSaving(true);

            const preset = result.template;
            const name = result.name;

            let templateData;
            if (['empty', 'default', 'recommended'].includes(preset) || String(preset).startsWith('builtin-')) {
                if (String(preset).startsWith('builtin-')) track('preset_use', { kind: 'pro-config', id: preset });
                templateData = getTemplateData(preset);
            } else {
                const templateCfg = configs.find(c => c.id === preset);
                templateData = templateCfg ? { ...templateCfg.settings } : getTemplateData('default');
            }

            const newConfig = {
                name: name.trim(),
                settings: templateData
            };

            try {
                await storageService.saveConfig(newConfig);
                await fetchUserConfigs(true);
            } catch (e) {
                console.error("Error creating config", e);
            } finally {
                if (setIsSaving) setIsSaving(false);
            }
        }
    };

    const handleRename = async (config) => {
        if (config.isLocked) return;

        const newName = await showPrompt({
            title: t('RENAME_CONFIG') || 'Renombrar Config',
            defaultValue: config.name,
            confirmText: t('RENAME_ACTION') || 'Renombrar',
            placeholder: t('CONFIG_NAME_LABEL')
        });

        if (!newName || newName.trim() === '' || newName === config.name) return;

        if (configs.some(c => c.name.toLowerCase() === newName.trim().toLowerCase() && c.id !== config.id)) {
            await showConfirm({
                title: t('ERROR'),
                message: t('CONFIG_NAME_EXISTS') || 'El nombre ya existe',
                confirmText: 'OK',
                variant: 'danger'
            });
            return;
        }

        try {
            const updated = { ...config, name: newName.trim() };
            await storageService.saveConfig(updated);
            await fetchUserConfigs(true);
            setOpenMenuId(null);
        } catch (e) {
            console.error(e);
        }
    };

    // New Handler for Binds in Table
    const handleLauncherBindChange = async (config, newBind) => {
        if (config.isLocked) return;
        await setLauncherBind(config, newBind);
    };


    const handleBatchDelete = async () => {
        const msg = t('CONFIRM_BATCH_DELETE').replace('{count}', selectedIds.length);
        const confirmed = await showConfirm({
            title: t('BATCH_DELETE'),
            message: msg,
            variant: 'danger',
            confirmText: t('BATCH_DELETE')
        });

        if (confirmed) {
            for (const id of selectedIds) {
                await deleteConfig(id);
            }
            setSelectedIds([]);
        }
    };

    const handleBatchLock = async (lockState) => {
        for (const id of selectedIds) {
            const config = configs.find(c => c.id === id);
            if (config) {
                const updated = {
                    ...config,
                    isLocked: lockState,
                    settings: {
                        ...config.settings,
                        _meta: { ...config.settings?._meta, isLocked: lockState }
                    }
                };
                await storageService.saveConfig(updated);
            }
        }
        await fetchUserConfigs(true);
        setSelectedIds([]);
    };

    return (
        <Container onClick={() => setOpenMenuId(null)} style={{ minHeight: 'calc(100vh - 140px)' }}>
            <SidebarTitle><span>{t('MANAGER_TITLE_PREFIX')}</span>{t('MANAGER_TITLE_SUFFIX')}</SidebarTitle>
            <ModulesSection>
                <ModulesSectionTitle>Config activa</ModulesSectionTitle>
                <ModulesGrid>
                    <PreviewActionCard onClick={() => router.push('/editor/crosshair')}>
                        <Edit style={{ fontSize: 28 }} />
                        <GridBtnLabel>Editar config</GridBtnLabel>
                        <GridBtnSub>Ir al editor</GridBtnSub>
                    </PreviewActionCard>
                    <CrosshairSidebarPreview
                        data={configData?.crosshair}
                        isActive={activeModules?.includes('crosshair')}
                        isDisabled={!configName}
                        onClick={() => handlePreviewClick('crosshair')}
                        t={t}
                        hideToggle={true}
                    />
                    <MousePreview
                        data={configData?.mouse}
                        isActive={activeModules?.includes('mouse')}
                        isDisabled={!configName}
                        onClick={() => handlePreviewClick('mouse')}
                        t={t}
                        hideToggle={true}
                    />
                    <ViewmodelPreview
                        data={configData?.viewmodel}
                        isActive={activeModules?.includes('viewmodel')}
                        isDisabled={!configName}
                        onClick={() => handlePreviewClick('viewmodel')}
                        t={t}
                        hideToggle={true}
                    />
                    <HudPreview
                        data={configData?.hud}
                        isActive={activeModules?.includes('hud')}
                        isDisabled={!configName}
                        onClick={() => handlePreviewClick('hud')}
                        t={t}
                        hideToggle={true}
                    />
                    
                </ModulesGrid>
            </ModulesSection>

            <TableContainer>
                <Table>
                    <Thead>
                        <Tr>
                            {isPro && (
                                <Th>
                                    <IconButton onClick={handleSelectAll}>
                                        {isAllSelected ? <CheckBox fontSize="small" style={{ color: '#6366f1' }} /> : <CheckBoxOutlineBlank fontSize="small" />}
                                    </IconButton>
                                </Th>
                            )}
                            <Th style={{ minWidth: '360px', textAlign: 'left' }}>{t('CONFIG_NAME_LABEL')}</Th>
                            <Th style={{ textAlign: 'center' }}>{t('TABLE_BIND')}</Th>
                            <Th style={{ textAlign: 'center' }}>{t('TABLE_UPDATED')}</Th>
                            <Th></Th>
                        </Tr>
                    </Thead>
                    <tbody>
                        {configs.map(cfg => {
                            const isActive = cfg.name === configName;
                            return (
                                <Tr
                                    key={cfg.id}
                                    $selected={selectedIds.includes(cfg.id)}
                                    $isActive={isActive}
                                    onClick={() => handleActivate(cfg)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {isPro && (
                                        <Td>
                                            <IconButton onClick={(e) => { e.stopPropagation(); handleSelectOne(cfg.id); }}>
                                                {selectedIds.includes(cfg.id) ? <CheckBox fontSize="small" style={{ color: '#6366f1' }} /> : <CheckBoxOutlineBlank fontSize="small" />}
                                            </IconButton>
                                        </Td>
                                    )}
                                    <Td>
                                        <ConfigName>
                                            {isActive && <CheckCircle style={{ fontSize: 16, color: '#4ade80' }} />}
                                            {cfg.isLocked && <Lock style={{ fontSize: 14, color: '#fbbf24' }} />}
                                            {cfg.name}
                                            {isActive && <Badge $color="rgba(74, 222, 128, 0.2)" $textColor="#4ade80" $borderColor="rgba(74, 222, 128, 0.3)">{t('SELECTED') || 'ACTIVE'}</Badge>}
                                            {cfg.isBuiltin && <Badge>SYSTEM</Badge>}
                                        </ConfigName>
                                    </Td>
                                    <Td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <KeySelector
                                                value={cfg.launcherBind}
                                                onChange={(val) => handleLauncherBindChange(cfg, val)}
                                                disabled={cfg.isLocked || cfg.isBuiltin}
                                                style={{ minWidth: '80px', height: '32px', fontSize: '0.75rem' }}
                                                fieldPath="_launcher"
                                            />
                                        </div>
                                    </Td>
                                    <Td style={{ textAlign: 'center', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
                                        {new Date(cfg.updated_at || Date.now()).toLocaleDateString()}
                                    </Td>
                                    <Td style={{ position: 'relative' }}>
                                        {isPro ? (
                                            <>
                                                <IconButton onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenMenuId(openMenuId === cfg.id ? null : cfg.id);
                                                }}>
                                                    <MoreVert />
                                                </IconButton>
                                                {openMenuId === cfg.id && (
                                                    <MenuDropdown>
                                                        <MenuItem onClick={(e) => { e.stopPropagation(); handleRename(cfg); }} style={{ opacity: cfg.isLocked ? 0.5 : 1, cursor: cfg.isLocked ? 'not-allowed' : 'pointer' }}>
                                                            <Edit fontSize="small" /> {t('RENAME_ACTION') || 'Renombrar'}
                                                        </MenuItem>
                                                        <MenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(cfg.id); }}>
                                                            <ContentCopy fontSize="small" /> {t('DUPLICATE_ACTION')}
                                                        </MenuItem>
                                                        <MenuItem onClick={(e) => { e.stopPropagation(); handleToggleLock(cfg); }}>
                                                            {cfg.isLocked ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
                                                            {cfg.isLocked ? t('UNLOCK_ACTION') : t('LOCK_ACTION')}
                                                        </MenuItem>
                                                        <MenuItem className="danger" onClick={(e) => { e.stopPropagation(); handleDelete(cfg.id); }}>
                                                            <DeleteIcon fontSize="small" /> {t('DELETE_ACTION')}
                                                        </MenuItem>
                                                    </MenuDropdown>
                                                )}
                                            </>
                                        ) : (
                                            <Lock fontSize="small" style={{ color: 'rgba(251, 191, 36, 0.4)', margin: '0 4px' }} />
                                        )}
                                    </Td>
                                </Tr>
                            )
                        })}
                        {configs.length === 0 && (
                            <Tr>
                                <Td colSpan={isPro ? 6 : 5} style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>
                                    {t('NO_CONFIGS_SAVED')}
                                </Td>
                            </Tr>
                        )}
                    </tbody>
                </Table>
            </TableContainer>

            <ActionRow>
                <GridActionBtn
                    $variant="edit"
                    $locked={!isPro}
                    disabled={!isPro}
                    onClick={isPro ? handleCreateConfig : undefined}
                >
                    {isPro
                        ? <Add style={{ fontSize: 22 }} />
                        : <Lock style={{ fontSize: 20, color: '#fbbf24' }} />}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <GridBtnLabel>Nueva config</GridBtnLabel>
                        <GridBtnSub>{isPro ? 'Crear configuración' : 'Requiere modo Pro'}</GridBtnSub>
                    </div>
                </GridActionBtn>
            </ActionRow>
            {!isPro && (
                <button
                    onClick={() => getBridge()?.openExternal('https://www.patreon.com/ConfigStrike')}
                    style={{
                        marginTop: 10,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 12,
                        color: 'rgba(251, 191, 36, 0.6)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        letterSpacing: '0.3px',
                        transition: 'color 0.2s ease',
                        padding: 0,
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = '#fbbf24'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(251, 191, 36, 0.6)'}
                >
                    ✦ Doná para desbloquear el modo Pro
                </button>
            )}

            <BatchBar $visible={selectedIds.length > 0}>
                <BatchCount>{t('SELECTED_FROM_TOTAL').replace('{count}', selectedIds.length)}</BatchCount>
                <BatchActions>
                    <BatchBtn onClick={() => handleBatchLock(true)}>
                        <Lock fontSize="small" /> {t('BATCH_LOCK')}
                    </BatchBtn>
                    <BatchBtn onClick={() => handleBatchLock(false)}>
                        <LockOpen fontSize="small" /> {t('BATCH_UNLOCK')}
                    </BatchBtn>
                    <BatchBtn className="danger" onClick={handleBatchDelete}>
                        <DeleteIcon fontSize="small" /> {t('BATCH_DELETE')}
                    </BatchBtn>
                </BatchActions>
            </BatchBar>


        </Container>
    );
};

export default ManagerView;
